import * as React from "react";

import { useOrderingAvailableForDay } from "src/core/orders/hooks/validation";
import {
  IOrder,
  IOrderVariant
} from "src/core/orders/api/orders.api";
import { IProduct } from "src/core/products/api/products.api";
import { HtmlBody } from "src/library/components/Notes";
import { Text } from "src/library/components/Text";
import { isSameDay } from "src/library/utils/date-helpers/index";
import { getSingleOrder } from "src/core/drops/utils/inherited-settings";
import { IDrop } from "src/core/drops/api/drops.api";

export const useDisabledDeliveryDate = (drop: IDrop | null): typeof dateIsDisabled => {
  const orderingNotAvailableForDay = useOrderingAvailableForDay(drop);

  /**
   * Evaluates a and blocks dates providing a reason as to why they are blocked
   */
  const dateIsDisabled = React.useCallback(
    (date: Date, currentOrders: IOrder[], orderVariant: IOrderVariant, product?: IProduct) => {
      // if the date matches a shutdown dont allow creation
      const dayOrder = currentOrders.find(currentOrder => isSameDay(currentOrder.orderDeliveryDate as Date, date));

      if (drop) {
        const oneOrderMax = getSingleOrder(drop?.groupedSettings);

        if (oneOrderMax?.value === true && dayOrder) {
          return {
            isDisabled: true,
            component: (
              <p className="disabled">
                This delivery date already has an order on, please amend this order instead
              </p>
            )
          };
        }
      }

      const orderingErrorMessage = orderingNotAvailableForDay(date, orderVariant, product);
      const orderingAvailable = orderingErrorMessage === null;

      return {
        isDisabled: !orderingAvailable,
        component: !orderingAvailable ? (
          <>
            {orderingErrorMessage ? (
              <HtmlBody
                className="disabled"
                body={orderingErrorMessage}
              />
            ) : (
              <Text className="disabled">
                Ordering not available for day.
              </Text>
            )}
          </>
        ) : undefined
      };
    },
    [ drop, orderingNotAvailableForDay ]
  );

  return dateIsDisabled;
};
